import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  useOutletContext,
  Navigate
} from 'react-router-dom';
import LandingPage from './LandingPage';
import DealerJourneyManager from './DealerJourneyManager';
import VehicleDetailsPage from './VehicleDetailsPage';
import ProductPage from './ProductPage';
import CreatePlanPage from './CreatePlanPage';
import PersonalDetailDealerPage from './PersonalDetailDealerPage';
import SummaryPage from './SummaryPage';
import DirectDebitSetup from './DirectDebitSetup';
import SinglePaymentPage from './SinglePaymentPage';
import Confirmation from './Confirmation';
import FailedRegistration from './FailedRegistration';
import UserNotFoundPage from './UserNotFoundPage';
import RenewalLandingPage from './renewals/landingPage/LandingPage';
import { Journey, PageAbsolutePath } from '../../consts/Common';
import { SiteApi } from '../../api/SiteApi';
import { JourneyContext, Site } from '../common/CommonTypes';
import { SiteContext } from '../../context/SiteContext';
import RenewalsJourneyManager from './renewals/RenewalsJourneyManager';
import RenewalsSummary from './renewals/RenewalsSummary';
import RenewalsConfirmation from './renewals/RenewalsConfirmation';
import RenewalsPersonalDetail from './renewals/RenewalsPersonalDetail';
import RenewalsQuote from './renewals/quotes/RenewalsQuote';
import RenewalsVehicleDetails from './renewals/RenewalsVehicleDetails';
import RenewalsDirectDebit from './renewals/RenewalsDirectDebit';
import RenewalsFailedRegistration from './renewals/FailedRegistration';
import LateRenewalAcceptance from './renewals/LateRenewalAcceptance';
//@ts-ignore
import TagManager from 'react-gtm-module';
import CustomerJourneyManager from './customer/CustomerJourneyManager';
import CustomerLandingPage from './customer/CustomerLandingPage';
import CustomerVehicleDetailsPage from './customer/CustomerVehicleDetailsPage';
import CustomerProductPage from './customer/CustomerProductPage';
import CustomerCreatePlanPage from './customer/CustomerCreatePlanPage';
import CustomerPersonalDetail from './customer/CustomerPersonalDetail';
import CustomerSummaryPage from './customer/CustomerSummaryPage';
import CustomerDirectDebitPage from './customer/CustomerDirectDebitPage';
import CustomerConfirmationPage from './customer/CustomerConfirmationPage';
import CircularProgress from '@mui/material/CircularProgress';
import AppUnderMaintenance from './AppUnderMaintenance';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import i18next from 'i18next';
import { setCountryCode } from '../common/HelperMethods';
import { useCurrentPath } from './useCurrentPath';
import { CountryIsoCodes, legacyUrls } from '../common/Constants';
import InsuranceLandingPage from './insurance/InsuranceLandingPage';
import AboutYouPage from './insurance/AboutYouPage';
import SiteMapPage from './insurance/SiteMapPage';
import MotorInsuranceClaimPage from './insurance/MotorInsuranceClaimPage';
import AncillaryPage, { HeroType } from './insurance/AncillaryPage';
import EligibilityAncillaryPage from './insurance/EligibilityAncillaryPage';
import FairUseNoticePage from './insurance/FairUseNoticePage';
import TermsUseAncillaryPage from './insurance/TermsUseAncillaryPage';
import BusinessTermsAncillaryPage from './insurance/BusinessTermsAncillaryPage';
import InsuranceJourneyManager from './insurance/InsuranceJourneyManager';
import TermsAndConditions from './insurance/TermsAndConditions';
import DrivingHistoryPage from './insurance/DrivingHistoryPage';
import CoverDetailsPage from './insurance/CoverDetailsPage';
import ElectricVehiclePage from './insurance/ElectricVehiclePage';
import InsuranceVehicleDetailsPage from './insurance/InsuranceVehicleDetailsPage';
import InsuranceCoverDetailsPage from './insurance/InsuranceCoverDetailsPage';
import ExcessReturnPolicyTermsConditions from './insurance/ExcessReturnPolicyTermsConditions';
import FaqsPage from './insurance/FaqsPage';
import RetrieveQuote from './insurance/RetrieveQuote';
import ProofOfNcd from './insurance/ProofOfNcd';
import RegisterForExcessReturn from './insurance/RegisterForExcessReturn';
import AdditionalDriverDetailsPage from './insurance/AdditionalDriverDetailsPage';
import AddressDetailsPage from './insurance/AddressDetailsPage';
import ConfirmationDetailsPage from './insurance/ConfirmationDetailsPage';
import Questionnaire from './insurance/Questionnaire';
import CustomerSelfRegistration from './insurance/CustomerSelfRegistration';
import DriveAwayAncillaryPage from './insurance/DriveAwayAncillaryPage';
import PolicyDocuments from './insurance/PolicyDocuments';
import DealerLandingPage from './insurance/dealer/DealerLandingPage';
import Login from './insurance/dealer/Login';
import DealersHomePage from './insurance/dealer/DealersHomepage';
import YourQuotePage from './insurance/YourQuotePage';
import OptionalCoverPage from './insurance/OptionalCoverPage';
import CheckYourDetailsPage from './insurance/CheckYourDetailsPage';
import PaymentPage from './insurance/PaymentPage';
import Referral from './insurance/Referral';
import PolicyConfirmationPage from './insurance/PolicyConfirmationPage';
import InsuranceFailedRegistration from './insurance/InsuranceFailedRegistration';
import DealerResourcesPage from './insurance/dealer/DealerResourcesPage';
import HelpPage from './insurance/HelpPage';
import AboutTermsOfBusinessAndFairUsePage from './insurance/AboutTermsOfBusinessAndFairUsePage';
import ImportantNoticeInformationPage from './insurance/ImportantNoticeInformationPage';
import DealerAdminPage from './insurance/dealer/DealerAdminPage';
import SettingUpDirectDebitPage from './insurance/SettingUpDirectDebitPage';
import UpdatePreferencesPage from './insurance/UpdatePreferencesPage';
import PostcodeConfirmationPage from './insurance/PostcodeConfirmationPage';
import AccidentAssistanceAncillaryPage from './insurance/AccidentAssistanceAncillaryPage';
import AlternateEligibility from './insurance/AlternateEligibility';
import OrderVerification from './insurance/OrderVerification';
import ServiceExcessPage from './insurance/ServiceExcessPage';
import ExcessConfirmationPage from './insurance/ExcessConfirmationPage';
import MyAccountAncillaryPage from './insurance/MyAccountAncillaryPage';
import DealerReferral from './insurance/DealerReferral';
import LotusSecurityPage from './insurance/LotusSecurityPage';
import LotusUpdateDetailsPage from './insurance/LotusUpdateDetailsPage';
import PaymentAssistSuccess from './PaymentAssistSuccess';
import PaymentAssistFailure from './PaymentAssistFailure.';
import { useTranslation } from 'react-i18next';

const Layout = (): JSX.Element => {
  const { site, setSite } = useContext(SiteContext);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const currentPath = useCurrentPath();
  const hostname = window.location.hostname;
  const [translationsReady, setTranslationsReady] = useState(false);
  const isLegacyInsuranceSite = legacyUrls.some((url) =>
    hostname.includes(url)
  );
  const { ready } = useTranslation();

  useEffect(() => {
    const countryCode = site.countryCode;
    let urlLanguage = 'en';
    switch (countryCode.toLowerCase()) {
      case CountryIsoCodes.GB:
        urlLanguage = 'en';
        break;
      case CountryIsoCodes.DE:
      case CountryIsoCodes.AT:
        urlLanguage = 'de-DE';
    }

    i18next.changeLanguage(urlLanguage).then(() => setTranslationsReady(true));
  }, [site.countryCode]);

  useEffect(() => {
    const abortController = new AbortController();

    async function fetchSite(
      journeyType: number,
      instanceId: string,
      signal: AbortSignal,
      renewalSourceSystem: string,
      recordId?: string,
      vPol?: string
    ) {
      const fetchSiteSuccess = (retrievedSite: Site) => {
        console.log(retrievedSite.tiaData);

        if (retrievedSite.isAppUnderMaintenance) {
          const path = setCountryCode(
            PageAbsolutePath.UnderMaintenance,
            site.countryCode
          );
          navigate(path, {
            state: {
              error: retrievedSite.maintenanceMessage
            }
          });
          setLoading(false);
        } else {
          if (
            retrievedSite.brand.brandStyles === '' &&
            journeyType !== Journey.Insurance
          ) {
            const path = setCountryCode(
              PageAbsolutePath.Error,
              site.countryCode
            );
            navigate(path, {
              state: {
                error: 'This brand is not configured for use in this site'
              }
            });
          }
          if (retrievedSite.user.error) {
            const path = setCountryCode(
              PageAbsolutePath.Error,
              site.countryCode
            );
            navigate(path, {
              state: {
                error: retrievedSite.user.error
              }
            });
          }
          if (
            journeyType === Journey.Renewals ||
            journeyType === Journey.Customer ||
            journeyType === Journey.Insurance ||
            retrievedSite?.user?.isValid === true
          ) {
            const dealerPagePathName = pathname?.toLowerCase().endsWith('/')
              ? pathname.slice(0, pathname.length - 1)
              : pathname;

            const isInsuranceJourneyDealer =
              dealerPagePathName ===
                PageAbsolutePath.Insurance.DealerLandingPage ||
              (isLegacyInsuranceSite &&
                dealerPagePathName ===
                  PageAbsolutePath.Insurance.OldDealerLanding);

            const isInsuranceJourneyMobilise = vPol !== '';

            setSite({
              ...retrievedSite,
              journeyType: journeyType,
              recordId: recordId,
              renewalSourceSystem: renewalSourceSystem,
              user: isInsuranceJourneyDealer
                ? { ...retrievedSite.user, isDealer: true }
                : { ...retrievedSite.user },
              isInsuranceMobilise: isInsuranceJourneyMobilise,
              vPol: vPol ?? ''
            });

            if (
              CommonValidation.isNotEmpty(
                retrievedSite.brand.googleTagManagerId
              )
            ) {
              const tagManagerArgs = {
                gtmId: retrievedSite.brand.googleTagManagerId
              };
              TagManager.initialize(tagManagerArgs);
            }
          } else {
            const path = setCountryCode(
              PageAbsolutePath.Error,
              site.countryCode
            );
            navigate(path, {
              state: { error: retrievedSite?.user?.error }
            });
          }

          setLoading(false);
        }
      };

      const fetchSiteFailure = (error: string) => {
        setLoading(false);
        const path = setCountryCode(PageAbsolutePath.Error, site.countryCode);
        navigate(path, {
          state: { error: 'User not found' }
        });
        console.log('User not found - ' + error);
      };

      await new SiteApi().getSiteDetails(
        journeyType,
        instanceId,
        fetchSiteSuccess,
        fetchSiteFailure,
        signal
      );
    }

    if (isPageReload()) {
      const path = setCountryCode(
        PageAbsolutePath.Renewals.Landing,
        site.countryCode
      );
      navigate(path);
    }

    if (CommonValidation.isEmpty(site?.user?.userId)) {
      const params = getParams();
      const instanceId = params.get('InstanceID');
      const recordId = params.get('RecordID');
      const renewalSourceSystem = params.get('RenewalSourceSystem');
      const vPol = params.get('vpol');

      const journeyType = getJourneyType(instanceId);

      if (journeyType === Journey.Initial) {
        const path = setCountryCode(PageAbsolutePath.Error, site.countryCode);
        navigate(path, {
          state: { error: 'User not found' }
        });
      } else {
        fetchSite(
          journeyType,
          instanceId ?? '',
          abortController.signal,
          renewalSourceSystem ?? '',
          recordId ?? '',
          vPol ?? ''
        );
      }
    }

    return () => {
      abortController.abort();
    };
  }, []);

  const getParams = () => {
    const result = new URLSearchParams(search);

    return result;
  };

  const InsuranceRedirects: string[] = [
    PageAbsolutePath.InsuranceRedirect.FairUseNotice
  ];

  const getJourneyType = (instanceId: string | null): number => {
    switch (true) {
      case instanceId && currentPath === PageAbsolutePath.Renewals.Landing:
        return Journey.CallCentre;
      case !isLegacyInsuranceSite &&
        instanceId &&
        (currentPath === PageAbsolutePath.Landing || currentPath === ''):
        return Journey.DealerJourney;
      case currentPath === PageAbsolutePath.Renewals.Landing:
        return Journey.Renewals;
      case currentPath === PageAbsolutePath.Landing:
      case currentPath === PageAbsolutePath.Customer.Landing:
      case currentPath === PageAbsolutePath.Customer.PaymentAssistSuccess:
      case currentPath === PageAbsolutePath.Customer.PaymentAssistFailure:
        return Journey.Customer;
      case currentPath === PageAbsolutePath.Insurance.Landing ||
        currentPath === PageAbsolutePath.Insurance.DealerLandingPage ||
        isLegacyInsuranceSite ||
        InsuranceRedirects.some((path) => currentPath === path):
        return Journey.Insurance;
      default:
        return Journey.Initial;
    }
  };

  const isPageReload = (): boolean => {
    const renewalPages = Object.values<string>(PageAbsolutePath.Renewals);
    const result = renewalPages.includes(window.location.pathname);
    let returnValue = false;

    if (result === true) {
      const navigationType = window.performance.navigation.type;
      if (navigationType == 1) {
        returnValue = true;
      }
    }

    return returnValue;
  };

  const iconUrl = site.content?.common?.faviconUrl ?? '';
  const force24Script = site.content?.force24Script ?? '';
  const allureScript = site.content?.allureScript ?? '';

  const electricVehiclePageTitle = `${site.brand.brandDisplayName} Electric Vehicle Insurance`;

  if (loading || !translationsReady || !ready) {
    return (
      <div className='loading-container'>
        <CircularProgress />
      </div>
    );
  }

  const getLandingPageRoutes = () => {
    if (!isLegacyInsuranceSite) {
      return (
        <Route path={PageAbsolutePath.Landing} element={<LandingPage />} />
      );
    }

    return (
      <>
        <Route
          path={PageAbsolutePath.Landing}
          element={
            <Navigate to={PageAbsolutePath.Insurance.Landing}></Navigate>
          }
        />
        <Route
          path={PageAbsolutePath.Insurance.OldDealerLanding}
          element={
            <Navigate
              to={PageAbsolutePath.Insurance.DealerLandingPage}></Navigate>
          }
        />
      </>
    );
  };

  return (
    <>
      <Helmet>
        <style>{site.brand.brandStyles}</style>
        <link rel='apple-touch-icon' href={iconUrl} />
        <link rel='icon' type='image/png' sizes='512x512' href={iconUrl} />
        <link rel='icon' type='image/png' sizes='192x192' href={iconUrl} />
        <link rel='icon' type='image/png' sizes='32x32' href={iconUrl} />
        <link rel='icon' type='image/png' sizes='16x16' href={iconUrl} />
        <link rel='shortcut icon' href={iconUrl} />
        <script type='text/javascript'>{force24Script}</script>
        <script type='text/javascript'>{allureScript}</script>
      </Helmet>
      <Routes>
        <Route
          path={PageAbsolutePath.Renewals.Landing}
          element={
            <RenewalLandingPage
              callCenterVrn={getParams()?.get('VRN') || ''}
              expAuthId={getParams()?.get('expauthid') || ''}
              campaignId={getParams()?.get('campaignid') || '0'}
            />
          }
        />
        <Route
          path=':countrycode?/renewal/renewalsjourney'
          element={<RenewalsJourneyManager />}>
          <Route path='personaldetails' element={<RenewalsPersonalDetail />} />
          <Route path='vehicledetails' element={<RenewalsVehicleDetails />} />
          <Route path='quote' element={<RenewalsQuote />} />
          <Route path='summary' element={<RenewalsSummary />} />
          <Route path='directdebit' element={<RenewalsDirectDebit />} />
          <Route path='singlepayment' element={<SinglePaymentPage />} />

          <Route path='confirmation' element={<RenewalsConfirmation />} />
          <Route
            path='failedregistration'
            element={<RenewalsFailedRegistration />}
          />
          <Route
            path='lateRenewalAcceptance'
            element={<LateRenewalAcceptance />}
          />
        </Route>
        <Route
          path={PageAbsolutePath.Customer.Landing}
          element={
            <CustomerLandingPage
              id={getParams()?.get('id') ?? ''}
              dealerNumber={getParams()?.get('dealercode') ?? ''}
            />
          }
        />
        <Route
          path=':countrycode?/customer/customerjourney'
          element={<CustomerJourneyManager />}>
          <Route
            path='vehicledetails'
            element={<CustomerVehicleDetailsPage />}
          />
          <Route path='products' element={<CustomerProductPage />} />
          <Route path='createplan' element={<CustomerCreatePlanPage />} />
          <Route path='personaldetails' element={<CustomerPersonalDetail />} />
          <Route path='summary' element={<CustomerSummaryPage />} />
          <Route path='directdebit' element={<CustomerDirectDebitPage />} />
          <Route path='singlepayment' element={<SinglePaymentPage />} />
          <Route path='confirmation' element={<CustomerConfirmationPage />} />
          <Route path='failedregistration' element={<FailedRegistration />} />
          <Route
            path='paymentassist/success'
            element={<PaymentAssistSuccess />}
          />
          <Route
            path='paymentassist/failure'
            element={<PaymentAssistFailure />}
          />
        </Route>
        {getLandingPageRoutes()}
        <Route
          path=':countrycode?/dealerjourney'
          element={<DealerJourneyManager />}>
          <Route path='vehicledetails' element={<VehicleDetailsPage />} />
          <Route path='products' element={<ProductPage />} />
          <Route path='createplan' element={<CreatePlanPage />} />
          <Route
            path='personaldetails'
            element={<PersonalDetailDealerPage />}
          />
          <Route path='summary' element={<SummaryPage />} />
          <Route path='directdebit' element={<DirectDebitSetup />} />
          <Route path='singlepayment' element={<SinglePaymentPage />} />
          <Route
            path='paymentassistsuccess'
            element={<PaymentAssistSuccess />}
          />
          <Route path='confirmation' element={<Confirmation />} />
          <Route path='failedregistration' element={<FailedRegistration />} />
        </Route>

        <Route path='/insurance' element={<InsuranceJourneyManager />}>
          <Route
            path={PageAbsolutePath.Insurance.DealerLandingPage}
            element={<DealerLandingPage />}
          />
          <Route
            path={PageAbsolutePath.Insurance.DealerLoginPage}
            element={<Login />}
          />
          <Route index element={<InsuranceLandingPage />} />
          <Route
            path='aboutyou'
            element={
              <Questionnaire>
                <AboutYouPage />
              </Questionnaire>
            }
          />
          <Route
            path='drivinghistory'
            element={
              <Questionnaire>
                <DrivingHistoryPage />
              </Questionnaire>
            }
          />
          <Route
            path='vehicledetails'
            element={
              <Questionnaire>
                <InsuranceVehicleDetailsPage />
              </Questionnaire>
            }
          />
          <Route
            path='insurancecoverdetails'
            element={
              <Questionnaire>
                <InsuranceCoverDetailsPage />
              </Questionnaire>
            }
          />
          <Route
            path='additionaldriverdetails'
            element={
              <Questionnaire>
                <AdditionalDriverDetailsPage />
              </Questionnaire>
            }
          />
          <Route
            path='addressdetailspage'
            element={
              <Questionnaire>
                <AddressDetailsPage />
              </Questionnaire>
            }
          />
          <Route
            path='confirmationdetails'
            element={
              <Questionnaire>
                <ConfirmationDetailsPage />
              </Questionnaire>
            }
          />
          <Route
            path='yourquote'
            element={
              <Questionnaire>
                <YourQuotePage />
              </Questionnaire>
            }
          />
          <Route
            path='optionalcover'
            element={
              <Questionnaire>
                <OptionalCoverPage />
              </Questionnaire>
            }
          />
          <Route
            path='checkyourdetails'
            element={
              <Questionnaire>
                <CheckYourDetailsPage />
              </Questionnaire>
            }
          />
          <Route
            path='paymentpage'
            element={
              <Questionnaire>
                <PaymentPage />
              </Questionnaire>
            }
          />
          <Route
            path='referral'
            element={
              <Questionnaire>
                <Referral />
              </Questionnaire>
            }
          />
          <Route
            path='dealerreferral'
            element={
              <Questionnaire>
                <DealerReferral />
              </Questionnaire>
            }
          />
          <Route
            path='policyconfirmation'
            element={
              <Questionnaire>
                <PolicyConfirmationPage />
              </Questionnaire>
            }
          />
          <Route
            path='failedregistration'
            element={
              <Questionnaire>
                <InsuranceFailedRegistration />
              </Questionnaire>
            }
          />
          <Route
            path={
              PageAbsolutePath.Insurance.AboutTermsOfBusinessAndFairUsageNotice
            }
            element={
              <Questionnaire showNavStepper={false}>
                <AboutTermsOfBusinessAndFairUsePage />
              </Questionnaire>
            }
          />
          <Route
            path={PageAbsolutePath.Insurance.ImportantNoticeInformation}
            element={
              <Questionnaire showNavStepper={false}>
                <ImportantNoticeInformationPage />
              </Questionnaire>
            }
          />
          <Route
            path={PageAbsolutePath.Insurance.SettingUpDirectDebit}
            element={
              <Questionnaire showNavStepper={false}>
                <SettingUpDirectDebitPage />
              </Questionnaire>
            }
          />
          <Route
            path='sitemap'
            element={
              <AncillaryPage title='Site Map'>
                <SiteMapPage />
              </AncillaryPage>
            }
          />
          <Route
            path='motorinsuranceclaim'
            element={
              <AncillaryPage title='Motor insurance claim'>
                <MotorInsuranceClaimPage />
              </AncillaryPage>
            }
          />
          <Route
            path='eligibilityancillarypage'
            element={
              <AncillaryPage title='Eligibility for insurance quote'>
                <EligibilityAncillaryPage />
              </AncillaryPage>
            }
          />
          <Route
            path='alternateeligibility'
            element={
              <AncillaryPage title='Eligibility'>
                <AlternateEligibility />
              </AncillaryPage>
            }
          />
          <Route
            path='orderverification'
            element={
              <AncillaryPage title={`Let's get started`}>
                <OrderVerification />
              </AncillaryPage>
            }
          />
          <Route
            path='fairUsenoticepage'
            element={
              <AncillaryPage title='Your Privacy'>
                <FairUseNoticePage />
              </AncillaryPage>
            }
          />
          <Route
            path='termsuseancillarypage'
            element={
              <AncillaryPage title='Website Terms Of Use'>
                <TermsUseAncillaryPage />
              </AncillaryPage>
            }
          />
          <Route
            path='businessancillarypage'
            element={
              <AncillaryPage title='Terms Of Business'>
                <BusinessTermsAncillaryPage />
              </AncillaryPage>
            }
          />
          <Route
            path='termsandconditions'
            element={
              <AncillaryPage title='Terms And Conditions'>
                <TermsAndConditions />
              </AncillaryPage>
            }
          />
          <Route
            path='coverdetailspage'
            element={
              <AncillaryPage title='Cover Details'>
                <CoverDetailsPage />
              </AncillaryPage>
            }
          />
          <Route
            path='electricvehiclepage'
            element={
              <AncillaryPage
                title={electricVehiclePageTitle}
                heroType={HeroType.Electric}>
                <ElectricVehiclePage />
              </AncillaryPage>
            }
          />
          <Route
            path='excessreturnpolicytermsconditions'
            element={
              <AncillaryPage title='Excess Return Policy Terms And Conditions'>
                <ExcessReturnPolicyTermsConditions />
              </AncillaryPage>
            }
          />
          <Route
            path='faqspage'
            element={
              <AncillaryPage title='FAQS'>
                <FaqsPage />
              </AncillaryPage>
            }
          />
          <Route
            path='retrievequote'
            element={
              <AncillaryPage title='Retrieve a quote'>
                <RetrieveQuote />
              </AncillaryPage>
            }
          />
          <Route
            path='lotussecuritypage'
            element={
              <AncillaryPage title='Lotus security page'>
                <LotusSecurityPage />
              </AncillaryPage>
            }
          />
          <Route
            path='lotusupdatedetails'
            element={
              <AncillaryPage title='Lotus update details page'>
                <LotusUpdateDetailsPage />
              </AncillaryPage>
            }
          />
          <Route
            path='proofOfncd'
            element={
              <AncillaryPage title='Proof of NCD'>
                <ProofOfNcd />
              </AncillaryPage>
            }
          />
          <Route
            path='registerforexcessreturn'
            element={
              <AncillaryPage title='Register for excess return'>
                <RegisterForExcessReturn />
              </AncillaryPage>
            }
          />
          <Route
            path='serviceExcessPage'
            element={
              <AncillaryPage title='Service excess'>
                <ServiceExcessPage />
              </AncillaryPage>
            }
          />
          <Route
            path='excessconfirmation'
            element={
              <AncillaryPage title='Excess confirmation'>
                <ExcessConfirmationPage />
              </AncillaryPage>
            }
          />
          <Route
            path='myaccount'
            element={
              <AncillaryPage title='My account'>
                <MyAccountAncillaryPage />
              </AncillaryPage>
            }
          />
          <Route
            path='customerselfregistration'
            element={
              <AncillaryPage title='Customer self registration'>
                <CustomerSelfRegistration />
              </AncillaryPage>
            }
          />
          <Route
            path='driveaway'
            element={
              <AncillaryPage title='Eligibility for Driveaway Insurance'>
                <DriveAwayAncillaryPage />
              </AncillaryPage>
            }
          />
          <Route
            path='accidentassistance'
            element={
              <AncillaryPage title='Accident Assitance'>
                <AccidentAssistanceAncillaryPage />
              </AncillaryPage>
            }
          />
          <Route
            path={PageAbsolutePath.Insurance.DealersHomePage}
            element={
              <AncillaryPage
                title={`Welcome ${site.user.insuranceDealershipName}, ${site.user.insuranceDealershipLocation}`}>
                <DealersHomePage />
              </AncillaryPage>
            }
          />
          <Route
            path={PageAbsolutePath.Insurance.DealersAdminPage}
            element={
              <AncillaryPage title={`Dealer dashboard`}>
                <DealerAdminPage />
              </AncillaryPage>
            }
          />
          <Route
            path={PageAbsolutePath.Insurance.DealersResources}
            element={
              <AncillaryPage title='Dealer resources'>
                <DealerResourcesPage />
              </AncillaryPage>
            }
          />
          <Route
            path='policydocuments'
            element={
              <AncillaryPage title='Policy documents'>
                <PolicyDocuments />
              </AncillaryPage>
            }
          />
          <Route
            path={PageAbsolutePath.Insurance.HelpPage}
            element={
              <AncillaryPage title='How can we help?'>
                <HelpPage />
              </AncillaryPage>
            }
          />
          <Route
            path='updatepreferencespage'
            element={
              <AncillaryPage title='Customer details'>
                <UpdatePreferencesPage />
              </AncillaryPage>
            }
          />
          <Route
            path={PageAbsolutePath.Insurance.PostcodeConfirmationPage}
            element={
              <AncillaryPage title='Update Your Preferences'>
                <PostcodeConfirmationPage />
              </AncillaryPage>
            }
          />
        </Route>
        {/*insurance route redirects*/}
        <Route
          path=':countrycode?/fairusenotice'
          element={
            <Navigate
              to={PageAbsolutePath.Insurance.FairUseNoticePage}
              state={{ navigateToLanding: true }}
              replace></Navigate>
          }
        />

        <Route
          path=':countrycode?/paymentassistfailure'
          element={<PaymentAssistFailure />}
        />
        <Route path=':countrycode?/errorpage' element={<UserNotFoundPage />} />
        <Route
          path=':countrycode?/undermaintenance'
          element={<AppUnderMaintenance />}
        />
      </Routes>
    </>
  );
};

export default Layout;

export function useJourney(): JourneyContext {
  return useOutletContext<JourneyContext>();
}
